import React, { useEffect, useMemo, useState, type ReactNode } from 'react'
import moment from 'moment'
import type { Moment } from 'moment'
import { connect, type ConnectedProps } from 'react-redux'
import type { AnyAction, Dispatch } from 'redux'
import { bindActionCreators } from 'redux'

import type { CalendarApiDayData } from '../../store/calendar'
import * as calendarActionCreators from '../../store/calendar'
import * as modalActionCreators from '../../store/modal'
import DayNames from './DayNames'
import Week from './Week'

type CalendarStateSlice = {
  month: Moment
  selected: Moment
  events: Record<string, CalendarApiDayData>
}

type AppState = {
  calendar: CalendarStateSlice
}

const mapStateToProps = (state: unknown) => {
  const rootState = state as AppState
  return {
    month: rootState.calendar.month,
    selected: rootState.calendar.selected,
    dayInfos: rootState.calendar.events,
  }
}

const mapDispatchToProps = (dispatch: Dispatch<AnyAction>) => {
  return {
    modalActions: bindActionCreators(modalActionCreators, dispatch),
    calendarActions: bindActionCreators(calendarActionCreators, dispatch),
  }
}

const connector = connect(mapStateToProps, mapDispatchToProps)

type PropsFromRedux = ConnectedProps<typeof connector>

function Calendar({
  month,
  selected,
  dayInfos,
  calendarActions,
  modalActions,
}: PropsFromRedux) {
  const [maxDate, setMaxDate] = useState(moment().add(2, 'years'))

  useEffect(() => {
    fetch('https://api.miniatur-wunderland.de/v1/maxDate/', {
      method: 'GET',
      mode: 'cors',
    })
      .then((response) => {
        response
          .json()
          .then((data: { maxDate: string }) => {
            setMaxDate(moment(data.maxDate, 'YYYY-MM-DD'))
          })
          .catch((e) => {
            console.warn(e)
          })
      })
      .catch((e) => {
        console.warn(e)
      })
  }, [])

  useEffect(() => {
    moment.locale(window.jsProps.lang)
    calendarActions.getEventsForMonth(month)
    calendarActions.getEventsForMonth(month.clone().add(1, 'month'))
    calendarActions.getEventsForMonth(month.clone().subtract(1, 'month'))
  }, [])

  const renderWeeks = () => {
    const weeks: ReactNode[] = []
    let done = false
    const date = month.clone().startOf('month').startOf('week')
    let count = 0
    let monthIndex = date.month()

    while (!done) {
      weeks.push(
        <Week
          key={date.format('YYYY-MM-DD')}
          date={date.clone()}
          month={month}
          dayInfos={dayInfos}
          selected={selected}
          onEventClick={(props) => modalActions.showModal('EventInfo', props)}
        />,
      )
      date.add(1, 'w')
      done = count++ > 2 && monthIndex !== date.month()
      monthIndex = date.month()
    }
    return weeks
  }

  const monthLabel = useMemo(() => {
    return (
      <div
        className="CalendarElement__MonthLabel"
        // onClick={() =>
        //   modalActions.showModal('DatePickerModal', {
        //     date: selected,
        //     maxDate: moment().add(10, 'years'),
        //     onChange: (date: Moment) => {
        //       calendarActions.selectDay(date)
        //       modalActions.hideModal()
        //       calendarActions.getEventsForMonth(date)
        //       calendarActions.getEventsForMonth(
        //         moment(date).clone().add(1, 'month'),
        //       )
        //       calendarActions.getEventsForMonth(
        //         moment(date).clone().subtract(1, 'month'),
        //       )
        //     },
        //   })
        // }
      >
        <div className="CalendarElement__MonthLabelText">
          {month.format('MMMM YYYY')}
        </div>
      </div>
    )
  }, [month])

  const prevMonth = useMemo(() => {
    const prevMonth = month.clone().subtract(1, 'month')

    if (prevMonth.isBefore(moment(), 'month')) {
      return (
        <div className="CalendarElement__MonthArrow CalendarElement__MonthArrow--left disabled">
          {prevMonth.format('MMMM')}
        </div>
      )
    }

    return (
      <div
        className="CalendarElement__MonthArrow CalendarElement__MonthArrow--left"
        onClick={calendarActions.prevMonth}
      >
        {prevMonth.format('MMMM')}
      </div>
    )
  }, [month])

  const nextMonth = useMemo(() => {
    const isAfter = maxDate
      ? month.clone().add(1, 'month').isAfter(maxDate)
      : false

    if (isAfter) {
      return (
        <button
          className="CalendarElement__MonthArrow CalendarElement__MonthArrow--right disabled"
          onClick={() => {
            alert(window.jsProps.strings.maxDate)
          }}
        >
          {month.clone().add(1, 'month').format('MMMM')}
        </button>
      )
    }

    return (
      <button
        className="CalendarElement__MonthArrow CalendarElement__MonthArrow--right"
        onClick={calendarActions.nextMonth}
      >
        {month.clone().add(1, 'month').format('MMMM')}
      </button>
    )
  }, [month, maxDate])

  return (
    <section className="CalendarElement">
      <header className="CalendarElement__Header">
        <div className="CalendarElement__MonthDisplay CalendarElement__Row">
          {prevMonth}
          {monthLabel}
          {nextMonth}
        </div>
        <DayNames />
      </header>
      <div className="CalendarElement__Weeks">{renderWeeks()}</div>
      <footer className="CalendarElement__Footer">
        <div className="CalendarElement__MonthDisplay CalendarElement__Row">
          {prevMonth}
          {monthLabel}
          {nextMonth}
        </div>
      </footer>
    </section>
  )
}

export default connector(Calendar)
